import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const UNTIL = "somnio_popup_until", DONE = "somnio_subscribed", SEEN = "somnio_popup_seen";
const SKIP = /^\/(checkout|payment|account|login|register|reset|unsubscribe|admin)/;

function canShow() {
  try {
    if (localStorage.getItem(DONE) || sessionStorage.getItem(SEEN)) return false;
    return Date.now() > Number(localStorage.getItem(UNTIL) || 0);
  } catch { return false; }
}

export function NewsletterPopup() {
  const { pathname } = useLocation();
  const { user } = useAuth() as any;
  const [c, setC] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"" | "busy" | "done">("");
  const [err, setErr] = useState("");
  const fired = useRef(false);
  useEffect(() => { api.get("/newsletter-popup").then((r) => setC(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!c?.enabled || fired.current || user?.role === "admin" || SKIP.test(pathname) || !canShow()) return;
    const fire = () => { if (fired.current || !canShow()) return; fired.current = true; try { sessionStorage.setItem(SEEN, "1"); } catch {} setOpen(true); cleanup(); };
    const t = window.setTimeout(fire, (c.delay ?? 20) * 1000);
    const onScroll = () => { const h = document.documentElement.scrollHeight - window.innerHeight; if (c.scroll > 0 && h > 0 && window.scrollY / h >= c.scroll / 100) fire(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const cleanup = () => { window.clearTimeout(t); window.removeEventListener("scroll", onScroll); };
    return cleanup;
  }, [c, pathname, user?.role]);
  useEffect(() => { if (open && SKIP.test(pathname)) setOpen(false); }, [pathname, open]);
  const close = () => { setOpen(false); try { localStorage.setItem(UNTIL, String(Date.now() + (c?.days || 1) * 86400000)); } catch {} };
  useEffect(() => { if (!open) return; const k = (e: KeyboardEvent) => e.key === "Escape" && close(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(""); setState("busy");
    try { await api.post("/newsletter", { email, source: "popup" }); setState("done"); try { localStorage.setItem(DONE, "1"); } catch {} }
    catch (x) { const m = errMsg(x); setErr(/valid email/i.test(m) ? "Please enter a valid email address." : m); setState(""); }
  };
  if (!open || !c) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-neutral-950/50 backdrop-blur-sm popup-fade" onClick={close} data-testid="newsletter-popup-overlay">
      <div role="dialog" aria-modal="true" aria-label={c.heading} className="relative w-full max-w-3xl bg-[var(--c-bg)] grid md:grid-cols-2 shadow-2xl popup-rise" onClick={(e) => e.stopPropagation()} data-testid="newsletter-popup">
        <button onClick={close} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-[var(--c-bg)]/80 hover:bg-white transition-colors" data-testid="newsletter-popup-close"><X size={16} /></button>
        {c.image && <img src={c.image} alt="" className="hidden md:block w-full h-full object-cover min-h-[420px]" />}
        <div className="p-8 md:p-10 flex flex-col justify-center">
          {state === "done" ? (
            <div data-testid="newsletter-popup-done">
              <p className="eyebrow text-[var(--c-primary)] mb-4">You're on the list</p>
              <h2 className="font-display text-3xl font-light leading-tight">Thank you — welcome to Somnio.Co</h2>
              <p className="mt-4 text-sm text-neutral-600">We'll remind you before every monthly draw. Check your inbox for a welcome note.</p>
              {c.gift_enabled && c.gift_url && <Link to={c.gift_url} onClick={close} className="btn-ink inline-block mt-7 px-7 py-3.5 text-xs tracking-[0.22em] uppercase" data-testid="newsletter-popup-gift">{c.gift_label || "Claim your gift"}</Link>}
              <button onClick={close} className="block mt-4 text-xs underline text-neutral-600" data-testid="newsletter-popup-continue">Continue browsing</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="newsletter-popup-form">
              {c.eyebrow && <p className="eyebrow text-[var(--c-primary)] mb-4">{c.eyebrow}</p>}
              <h2 className="font-display text-3xl md:text-4xl font-light leading-tight" data-testid="newsletter-popup-heading">{c.heading}</h2>
              {c.text && <p className="mt-4 text-sm text-neutral-600 leading-relaxed">{c.text}</p>}
              {c.gift_enabled && c.gift_text && <p className="mt-3 text-sm text-neutral-800" data-testid="newsletter-popup-gift-text">{c.gift_text}</p>}
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="mt-6 w-full border border-neutral-300 focus:border-neutral-900 outline-none bg-white px-4 py-3.5 text-sm transition-colors" data-testid="newsletter-popup-email" />
              {err && <p className="mt-2 text-xs text-red-600" data-testid="newsletter-popup-error">{err}</p>}
              <button disabled={state === "busy"} className="btn-ink w-full mt-3 py-4 text-xs tracking-[0.25em] uppercase disabled:opacity-60" data-testid="newsletter-popup-submit">{state === "busy" ? "Signing you up…" : c.button}</button>
              <button type="button" onClick={close} className="mt-4 text-xs text-neutral-500 underline" data-testid="newsletter-popup-no-thanks">No thanks</button>
              <p className="mt-4 text-[10px] text-neutral-400">Unsubscribe any time. <Link to="/pages/giveaway-terms" onClick={close} className="underline">Giveaway terms</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
